import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";

import {
  useNavigate,
  useLocation,
} from "react-router-dom";

import India from "@svg-maps/india";
import "./map.scss";
import { templesData } from "../../data/templesData.js";
import {
  allStatesData,
  allCitiesData,
  doesStateHaveTemples,
  getStateTemples,
  allActiveStatesWithCounts
} from "../../data/indiaLocationsData.js";
import SEO from "../../seo/SEO";

// =====================================================
// REGIONS
// =====================================================

const regions = [
  {
    title: "North India",
    slug: "north-india",
    description:
      "Discover ancient temples and sacred destinations across North India.",
    states:
      "Uttar Pradesh • Uttarakhand • Rajasthan • Himachal Pradesh",
    statesList: ["Uttar Pradesh", "Uttarakhand", "Rajasthan", "Himachal Pradesh"],
    image:
      "/assets/trending/kedarnath.webp",
  },

  {
    title: "South India",
    slug: "south-india",
    description:
      "Explore magnificent Dravidian temples and spiritual cities.",
    states:
      "Tamil Nadu • Karnataka • Kerala • Andhra Pradesh",
    statesList: ["Tamil Nadu", "Karnataka", "Kerala", "Andhra Pradesh"],
    image:
      "/assets/trending/rameshwaram.webp",
  },

  {
    title: "West India",
    slug: "west-india",
    description:
      "Journey through some of India's most iconic pilgrimage destinations.",
    states:
      "Gujarat • Maharashtra • Goa • Rajasthan",
    statesList: ["Gujarat", "Maharashtra", "Goa", "Rajasthan"],
    image:
      "/assets/trending/dwarka.webp",
  },

  {
    title: "East India",
    slug: "east-india",
    description:
      "Explore sacred temples, traditions and spiritual heritage.",
    states:
      "Odisha • West Bengal • Jharkhand • Bihar",
    statesList: ["Odisha", "West Bengal", "Jharkhand", "Bihar"],
    image:
      "/assets/trending/jaganath-puri.webp",
  },

  {
    title: "Central India",
    slug: "central-india",
    description:
      "Discover ancient shrines and hidden spiritual destinations.",
    states:
      "Madhya Pradesh • Chhattisgarh",
    statesList: ["Madhya Pradesh", "Chhattisgarh"],
    image:
      "/assets/trending/mahakaleshwar.webp",
  },

  {
    title: "Northeast India",
    slug: "northeast-india",
    description:
      "Experience unique temples and sacred traditions of Northeast India.",
    states:
      "Assam • Sikkim • Tripura • Manipur",
    statesList: ["Assam", "Sikkim", "Tripura", "Manipur"],
    image:
      "/assets/trending/kamakhya.webp",
  },
];

// Helper to reliably match state names (e.g. "Jammu and Kashmir" vs "Jammu & Kashmir")
const normalizeState = (name) => {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "")
    .trim();
};

const isStateMatching = (stateA, stateB) => {
  if (!stateA || !stateB) return false;
  const a = normalizeState(stateA);
  const b = normalizeState(stateB);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a))) return true;
  return false;
};

// =====================================================
// COMPONENT
// =====================================================

const IndiaMap = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const mapWrapperRef = useRef(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const pillsScrollerRef = useRef(null);

  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 0,
    top: 0,
  });

  // Highlighted states (all states clicked by the user) that persist on browser back
  const [clickedStates, setClickedStates] = useState(() => {
    try {
      const saved = sessionStorage.getItem("mw_clicked_states");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
      const single = sessionStorage.getItem("mw_selected_state");
      return single ? [single] : [];
    } catch {
      return [];
    }
  });

  // The latest active state clicked
  const [activeState, setActiveState] = useState(() => {
    try {
      return sessionStorage.getItem("mw_selected_state") || "";
    } catch {
      return "";
    }
  });

  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all"); // 'all' | 'temple' | 'state' | 'city'
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // State pills compact scroller
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Sync selected state from URL query parameters if available
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const urlState = params.get("state") || params.get("search");
      if (urlState) {
        setClickedStates((prev) => {
          if (prev.some((s) => isStateMatching(s, urlState))) return prev;
          const next = [...prev, urlState];
          try {
            sessionStorage.setItem("mw_clicked_states", JSON.stringify(next));
          } catch (e) {}
          return next;
        });
        setActiveState(urlState);
        sessionStorage.setItem("mw_selected_state", urlState);
      }
    } catch (e) {
      console.error(e);
    }
  }, [location.search]);

  // Auto-scroll the scroller to the active state pill when returning or selecting
  useEffect(() => {
    if (!activeState && clickedStates.length === 0) return;
    const timer = setTimeout(() => {
      const scroller = pillsScrollerRef.current;
      if (!scroller) return;
      const targetPill =
        scroller.querySelector(".stateFilterPill.latestActive") ||
        scroller.querySelector(".stateFilterPill.active");
      if (targetPill) {
        const targetScroll = targetPill.offsetLeft - scroller.offsetLeft - 30;
        scroller.scrollTo({
          left: Math.max(0, targetScroll),
          behavior: "smooth",
        });
        updatePillScrollState();
      }
    }, 180);

    return () => clearTimeout(timer);
  }, [activeState, clickedStates]);

  const updatePillScrollState = () => {
    const el = pillsScrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 6);
  };

  useEffect(() => {
    updatePillScrollState();
    const handleResize = () => updatePillScrollState();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scrollPills = (direction) => {
    const el = pillsScrollerRef.current;
    if (!el) return;
    const scrollAmount = direction === "left" ? -300 : 300;
    el.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const handlePillsWheel = (e) => {
    const el = pillsScrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && e.deltaY !== 0) {
      el.scrollLeft += e.deltaY;
      updatePillScrollState();
    }
  };

  // ===================================================
  // ALL SEARCHABLE ITEMS (TEMPLES + STATES + CITIES)
  // ===================================================

  const allSearchableItems = useMemo(() => {
    // 1. Priority Temples matching Listing.jsx
    const priorityTemples = [
      {
        type: "temple",
        name: "Shri Kedarnath Temple",
        location: "Kedarnath, Rudraprayag, Uttarakhand",
        route: "/kedarnath",
        image: "/assets/banner/vr-darshan/temples/kedarnath.webp",
        slug: "kedarnath-temple",
        deity: "Lord Shiva (Kedarnath Jyotirlinga)",
        keywords: ["shiva", "kedar", "uttarakhand", "char dham", "jyotirlinga", "himalayas", "bhole", "mahadev", "rudraprayag"]
      },
      {
        type: "temple",
        name: "Mata Vaishno Devi Temple",
        location: "Katra, Jammu & Kashmir",
        route: "/vaishno-devi",
        image: "/assets/trending/vaishno-devi.webp",
        slug: "vaishno-devi",
        deity: "Mata Vaishno Devi",
        keywords: ["vaishno devi", "katra", "mata rani", "durga", "jammu", "bhavan", "shrine", "devi", "jammu & kashmir"]
      },
      {
        type: "temple",
        name: "Kashi Vishwanath Temple",
        location: "Varanasi, Uttar Pradesh",
        route: "/kashi-vishwanath",
        image: "/assets/trending/kashi-vishwanath.webp",
        slug: "kashi-vishwanath",
        deity: "Lord Shiva",
        keywords: ["kashi", "vishwanath", "banaras", "varanasi", "ganga", "shiva", "jyotirlinga", "mahadev", "uttar pradesh"]
      },
      {
        type: "temple",
        name: "Ayodhya Ram Mandir (Shri Ram Janmabhoomi)",
        location: "Ayodhya, Uttar Pradesh",
        route: "/ayodhya",
        image: "/assets/trending/ayodhya-mandir.webp",
        slug: "ayodhya",
        deity: "Lord Ram",
        keywords: ["ram", "ayodhya", "janmabhoomi", "shri ram", "mandir", "uttar pradesh", "ram lalla"]
      },
      {
        type: "temple",
        name: "Dwarkadhish Temple",
        location: "Dwarka, Gujarat",
        route: "/dwarkadhish-temple",
        image: "/assets/trending/dwarkadhish.webp",
        slug: "dwarkadhish-temple",
        deity: "Lord Krishna",
        keywords: ["dwarka", "krishna", "gujarat", "char dham", "dwarkadhish", "mathura"]
      },
      {
        type: "temple",
        name: "Ramanathaswamy Temple",
        location: "Rameswaram, Tamil Nadu",
        route: "/ramanathaswamy-temple",
        image: "/assets/trending/rameshwaram.webp",
        slug: "ramanathaswamy-temple",
        deity: "Lord Shiva",
        keywords: ["rameswaram", "rameshwaram", "ramanathaswamy", "shiva", "tamil nadu", "char dham", "jyotirlinga"]
      },
      {
        type: "temple",
        name: "Amarnath Cave Temple",
        location: "Anantnag, Jammu & Kashmir",
        route: "/amarnath",
        image: "/assets/tample/amarnath-temple.svg",
        slug: "amarnath",
        deity: "Lord Shiva",
        keywords: ["amarnath", "baba barfani", "shiva", "kashmir", "cave", "himalayas", "yatra", "jammu & kashmir"]
      }
    ];

    const prioritySlugs = new Set(priorityTemples.map((t) => t.slug));

    // 2. All Temples from templesData
    const datasetTemples = templesData
      .filter((t) => !prioritySlugs.has(t.slug))
      .map((t) => {
        const loc = t.generalInfo?.location || "";
        const deity = t.generalInfo?.deity || "";
        const slugWords = (t.slug || "").split("-").filter(Boolean);
        const locWords = loc.toLowerCase().split(/[\s,()]+/).filter(Boolean);
        const deityWords = deity.toLowerCase().split(/[\s,()]+/).filter(Boolean);

        return {
          type: "temple",
          name: t.generalInfo?.templeName || "",
          location: loc,
          route: `/temple/${t.slug}`,
          image: t.images?.thumbnailImage || t.images?.timingImage || "/assets/banner/detail-image.svg",
          slug: t.slug,
          deity: deity,
          keywords: [
            deity,
            loc,
            t.slug,
            ...slugWords,
            ...locWords,
            ...deityWords
          ].filter(Boolean)
        };
      });

    // 3. All 36 States & Union Territories of India
    const statesList = allStatesData.map((s) => ({
      type: "state",
      name: s.name,
      id: s.id,
      location: s.location || "State • India",
      image: s.image,
      route: s.route,
      keywords: s.keywords || [s.name, "state", "india"]
    }));

    // 4. 168+ Sacred Cities & Cultural Towns of India
    const citiesList = allCitiesData.map((c) => ({
      type: "city",
      name: c.name,
      state: c.state,
      query: c.query,
      location: `${c.state}, India`,
      image: c.image,
      route: `/listing?search=${encodeURIComponent(c.query || c.name)}`,
      keywords: [...(c.keywords || []), "city", "sacred", c.state]
    }));

    return [
      ...priorityTemples,
      ...datasetTemples,
      ...statesList,
      ...citiesList
    ];
  }, []);

  // Filtered items based on search and activeCategory
  const filteredItems = useMemo(() => {
    let list = allSearchableItems;

    if (activeCategory !== "all") {
      list = list.filter((item) => item.type === activeCategory);
    }

    const query = searchValue.toLowerCase().trim();
    if (!query) {
      return list;
    }

    const queryWords = query.split(/\s+/).filter(Boolean);

    return list.filter((item) => {
      const name = (item.name || "").toLowerCase();
      const loc = (item.location || "").toLowerCase();
      const deity = (item.deity || "").toLowerCase();
      const state = (item.state || "").toLowerCase();
      const keywords = (item.keywords || []).map((k) => (k || "").toLowerCase());

      if (name.includes(query) || loc.includes(query) || deity.includes(query) || state.includes(query)) {
        return true;
      }

      if (keywords.some((k) => k.includes(query))) {
        return true;
      }

      const combinedText = `${name} ${loc} ${deity} ${state} ${keywords.join(" ")}`;
      return queryWords.every((word) => combinedText.includes(word));
    });
  }, [searchValue, activeCategory, allSearchableItems]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const query = searchValue.toLowerCase().trim();
    const queryWords = query ? query.split(/\s+/).filter(Boolean) : [];

    const matchesQuery = (item) => {
      if (!query) return true;
      const name = (item.name || "").toLowerCase();
      const loc = (item.location || "").toLowerCase();
      const deity = (item.deity || "").toLowerCase();
      const state = (item.state || "").toLowerCase();
      const keywords = (item.keywords || []).map((k) => (k || "").toLowerCase());

      if (name.includes(query) || loc.includes(query) || deity.includes(query) || state.includes(query)) {
        return true;
      }
      if (keywords.some((k) => k.includes(query))) {
        return true;
      }
      const combinedText = `${name} ${loc} ${deity} ${state} ${keywords.join(" ")}`;
      return queryWords.every((word) => combinedText.includes(word));
    };

    const matched = allSearchableItems.filter(matchesQuery);

    return {
      all: matched.length,
      temple: matched.filter((i) => i.type === "temple").length,
      state: matched.filter((i) => i.type === "state").length,
      city: matched.filter((i) => i.type === "city").length,
    };
  }, [searchValue, allSearchableItems]);

  // Outside click listener for suggestions and category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);


  // ===================================================
  // STATE HOVER
  // ===================================================

  const handleMouseEnter = (event, location) => {
    const stateElement = event.currentTarget;
    const wrapper = mapWrapperRef.current;
    if (!stateElement || !wrapper) {
      return;
    }

    const stateRect = stateElement.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    const stateCenterX = stateRect.left + stateRect.width / 2;
    const stateTop = stateRect.top;

    let left = stateCenterX - wrapperRect.left;
    let top = stateTop - wrapperRect.top - 14;

    const tooltipWidth = 210;
    const halfTooltip = tooltipWidth / 2;

    if (left < halfTooltip) {
      left = halfTooltip;
    }
    if (left > wrapperRect.width - halfTooltip) {
      left = wrapperRect.width - halfTooltip;
    }
    if (top < 60) {
      top = 60;
    }

    setHoveredState(location.name);
    setTooltipPosition({
      left,
      top,
    });
  };

  const handleMouseLeave = () => {
    setHoveredState(null);
  };

  const recordClickedState = (stateName) => {
    if (!stateName) return;
    setClickedStates((prev) => {
      const exists = prev.some((st) => isStateMatching(st, stateName));
      const next = exists ? prev : [...prev, stateName];
      try {
        sessionStorage.setItem("mw_clicked_states", JSON.stringify(next));
      } catch (e) {
        console.error(e);
      }
      return next;
    });

    setActiveState(stateName);
    try {
      sessionStorage.setItem("mw_selected_state", stateName);
    } catch (e) {
      console.error(e);
    }
  };

  const handleStateClick = (location) => {
    const stateName = location.name;
    recordClickedState(stateName);

    if (!doesStateHaveTemples(stateName)) {
      navigate("/coming-soon");
      return;
    }
    navigate(`/listing?search=${encodeURIComponent(stateName)}`);
  };

  const handleStateFilterClick = (stateName) => {
    recordClickedState(stateName);
    navigate(`/listing?search=${encodeURIComponent(stateName)}`);
  };

  const handleAllTemplesClick = () => {
    try {
      sessionStorage.removeItem("mw_clicked_states");
      sessionStorage.removeItem("mw_selected_state");
    } catch (e) {
      console.error(e);
    }
    setClickedStates([]);
    setActiveState("");
    navigate("/listing");
  };

  const handleClearAllClickedStates = (e) => {
    if (e) e.stopPropagation();
    try {
      sessionStorage.removeItem("mw_clicked_states");
      sessionStorage.removeItem("mw_selected_state");
    } catch (e) {
      console.error(e);
    }
    setClickedStates([]);
    setActiveState("");
  };

  const handleSelectItem = (item) => {
    setSearchValue(item.name);
    setShowSuggestions(false);
    if (item.type === "state") {
      recordClickedState(item.name);
    }
    navigate(item.route);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setShowCategoryDropdown(false);
    setShowSuggestions(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getHeaderTitle = () => {
    const count = filteredItems.length;
    const isSearching = searchValue.trim().length > 0;

    if (activeCategory === "temple") {
      return isSearching
        ? `${count} ${count === 1 ? "Temple" : "Temples"} Found`
        : `ALL TEMPLES (${count})`;
    }
    if (activeCategory === "state") {
      return isSearching
        ? `${count} ${count === 1 ? "State" : "States"} Found`
        : `ALL STATES (${count})`;
    }
    if (activeCategory === "city") {
      return isSearching
        ? `${count} ${count === 1 ? "City" : "Cities"} Found`
        : `ALL SACRED CITIES (${count})`;
    }
    return isSearching
      ? `${count} ${count === 1 ? "Result" : "Results"} Found`
      : `ALL LOCATIONS & TEMPLES (${count})`;
  };

  const searchPlaceholder = useMemo(() => {
    if (activeCategory === "temple") return "Search a temple (e.g. Kedarnath, Kashi, Somnath)...";
    if (activeCategory === "state") return "Search an Indian state (e.g. Uttar Pradesh, Gujarat)...";
    if (activeCategory === "city") return "Search a sacred city (e.g. Varanasi, Ayodhya, Puri)...";
    return "Search a state, city or temple...";
  }, [activeCategory]);

  // ===================================================
  // SEARCH
  // ===================================================

  const handleSearch = (event) => {
    if (event) event.preventDefault();

    const value = searchValue.trim();
    if (!value) {
      handleAllTemplesClick();
      return;
    }

    if (filteredItems.length > 0) {
      const topItem = filteredItems[0];
      if (topItem.type === "state") {
        recordClickedState(topItem.name);
      }
      navigate(topItem.route);
    } else {
      navigate("/coming-soon");
    }

    setShowSuggestions(false);
  };


  // ===================================================
  // RENDER
  // ===================================================

  return (
    <main className="indiaTemplePage">
      <SEO pageKey="indiaMap" />


      {/* ==================================================
          HERO
      ================================================== */}

      <section className="templeMapHero pb-0 pt-4">

        <div className="heroGlow heroGlowOne"></div>

        <div className="heroGlow heroGlowTwo"></div>


        <div className="container">

          <div className="heroContent">

           


            <h1>
              Explore Temples <span>Across India</span>
            </h1>


            <p>
              Discover sacred temples,
              spiritual destinations and
              ancient pilgrimage sites
              across every state of India.
            </p>


            {/* SEARCH */}
            <div className="templeSearchWrapper" ref={searchRef}>
              <form
                className="templeSearch"
                onSubmit={handleSearch}
              >
                {/* Category Filter Dropdown inside Search Bar */}
                <div className="searchCategoryDropdownWrapper">
                  <button
                    type="button"
                    className="categoryDropdownToggle"
                    onClick={() => {
                      setShowCategoryDropdown(!showCategoryDropdown);
                      setShowSuggestions(false);
                    }}
                    aria-label="Filter by category"
                  >
                    <span className="catActiveLabel">
                      {activeCategory === "all" && `All (${categoryCounts.all})`}
                      {activeCategory === "temple" && `Temples (${categoryCounts.temple})`}
                      {activeCategory === "state" && `States (${categoryCounts.state})`}
                      {activeCategory === "city" && `Cities (${categoryCounts.city})`}
                    </span>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: showCategoryDropdown ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease"
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {showCategoryDropdown && (
                    <div className="categoryDropdownMenu">
                      <button
                        type="button"
                        className={`catOption ${activeCategory === "all" ? "active" : ""}`}
                        onClick={() => handleCategoryChange("all")}
                      >
                        <span>All</span>
                        <span className="catCountBadge">{categoryCounts.all}</span>
                      </button>
                      <button
                        type="button"
                        className={`catOption ${activeCategory === "temple" ? "active" : ""}`}
                        onClick={() => handleCategoryChange("temple")}
                      >
                        <span>Temples</span>
                        <span className="catCountBadge">{categoryCounts.temple}</span>
                      </button>
                      <button
                        type="button"
                        className={`catOption ${activeCategory === "state" ? "active" : ""}`}
                        onClick={() => handleCategoryChange("state")}
                      >
                        <span>States</span>
                        <span className="catCountBadge">{categoryCounts.state}</span>
                      </button>
                      <button
                        type="button"
                        className={`catOption ${activeCategory === "city" ? "active" : ""}`}
                        onClick={() => handleCategoryChange("city")}
                      >
                        <span>Cities</span>
                        <span className="catCountBadge">{categoryCounts.city}</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="searchDivider" />

                <div className="searchField">
                  <span className="searchIcon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="7.5" />
                      <line x1="21" y1="21" x2="16.5" y2="16.5" />
                    </svg>
                  </span>
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchValue}
                    onChange={(event) => {
                      setSearchValue(event.target.value);
                      setShowSuggestions(true);
                      setShowCategoryDropdown(false);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setShowSuggestions(false);
                        setShowCategoryDropdown(false);
                      }
                    }}
                    placeholder={searchPlaceholder}
                  />
                  {searchValue && (
                    <button
                      type="button"
                      className="clearSearchBtn"
                      onClick={() => setSearchValue("")}
                      aria-label="Clear search"
                    >
                      &times;
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="searchSubmitBtn"
                >
                  Search
                </button>
              </form>

              {showSuggestions && (
                <div className="searchSuggestionsDropdown">
                  <div className="suggestionsHeader">
                    {getHeaderTitle()}
                  </div>

                  {filteredItems.length > 0 ? (
                    <ul className="suggestionsList">
                      {filteredItems.map((item, idx) => (
                        <li
                          key={`${item.type}-${item.name}-${idx}`}
                          className="suggestionItem"
                          onClick={() => handleSelectItem(item)}
                        >
                          <img
                            src={item.image || "/assets/trending/kedarnath.webp"}
                            alt={item.name}
                            className="suggestionThumb"
                            onError={(e) => {
                              e.target.src = "/assets/trending/kedarnath.webp";
                            }}
                          />

                          <div className="suggestionInfo">
                            <div className="suggestionTitleRow">
                              <span className="suggestionName">{item.name}</span>
                              <span className={`typeBadge badge-${item.type}`}>
                                {item.type === "temple" ? "Temple" : item.type === "state" ? "State" : "City"}
                              </span>
                            </div>
                            <span className="suggestionLocation">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="#00c2af">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                              </svg>
                              {item.location}
                            </span>
                          </div>

                          <span className="suggestionArrow">
                            &rarr;
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="noResultsFound">
                      <p>No results found matching "{searchValue}" in {activeCategory === "all" ? "any category" : activeCategory}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCategory("all");
                          setShowSuggestions(true);
                        }}
                      >
                        Search across All Categories
                      </button>
                    </div>
                  )}

                  <div className="suggestionsFooter">
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/india-temples");
                        setShowSuggestions(false);
                      }}
                    >
                      Browse All Temples in Directory &rarr;
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="heroHint">
              Try searching:
              <button
                type="button"
                onClick={() => {
                  setActiveCategory("state");
                  setSearchValue("Uttar Pradesh");
                  setShowSuggestions(true);
                }}
              >
                Uttar Pradesh
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveCategory("temple");
                  setSearchValue("Kedarnath");
                  setShowSuggestions(true);
                }}
              >
                Kedarnath
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveCategory("city");
                  setSearchValue("Varanasi");
                  setShowSuggestions(true);
                }}
              >
                Varanasi
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveCategory("state");
                  setSearchValue("Gujarat");
                  setShowSuggestions(true);
                }}
              >
                Gujarat
              </button>
            </div>

          </div>

        </div>

      </section>


      {/* ==================================================
          INDIA MAP
      ================================================== */}

      <section className="indiaMapSection">

        <div className="container">


          <div className="sectionHeading">
            <span className="sectionBadge">
              Explore by State
            </span>

            <h2>
              Choose a State
            </h2>

            <p>
              Hover over a state to see its sacred temples, and click to view and explore all temples connected to that region.
            </p>

          </div>

          {/* QUICK STATE FILTER PILLS (COMPACT SINGLE ROW) */}
          <div className="mapStatePillsBar">
            <div className="pillsSingleBar">
              {/* Pinned "All Temples" button - always visible, never slides */}
              <button
                type="button"
                className={`stateFilterPill pillPinned ${clickedStates.length === 0 ? "active" : ""}`}
                onClick={handleAllTemplesClick}
              >
                <span className="pillName">All Temples</span>
                <span className="pillCount">51</span>
              </button>

              <div className="pillsDivider" />

              {/* Scrollable track for States */}
              <div className="pillsSliderOuter">
                {/* Left arrow (appears near Uttar Pradesh when scrolled) */}
                {canScrollLeft && (
                  <button
                    type="button"
                    className="pillsNavArrow prev"
                    onClick={() => scrollPills("left")}
                    aria-label="Scroll left"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                )}

                {/* Edge fade gradient left */}
                {canScrollLeft && (
                  <div className="pillsFadeMask fadeLeft" />
                )}

                {/* Scroller Track for states */}
                <div
                  className="statePillsScroller"
                  ref={pillsScrollerRef}
                  onScroll={updatePillScrollState}
                  onWheel={handlePillsWheel}
                >
                  {allActiveStatesWithCounts.map((st) => {
                    const isPillHighlighted = clickedStates.some((s) => isStateMatching(s, st.name));
                    const isPillLatest = isStateMatching(activeState, st.name);
                    return (
                      <button
                        key={st.name}
                        type="button"
                        className={`stateFilterPill ${isPillHighlighted ? "active" : ""} ${isPillLatest ? "latestActive" : ""}`}
                        onClick={() => handleStateFilterClick(st.name)}
                      >
                        <span className="pillName">{st.name}</span>
                        <span className="pillCount">{st.count}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Edge fade gradient right */}
                {canScrollRight && (
                  <div className="pillsFadeMask fadeRight" />
                )}

                {/* Right arrow */}
                {canScrollRight && (
                  <button
                    type="button"
                    className="pillsNavArrow next"
                    onClick={() => scrollPills("right")}
                    aria-label="Scroll right"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* MAP CARD */}
          <div className="indiaMapOuter">
            <div className="mapGlow"></div>

            <div
              className="indiaMapWrapper"
              ref={mapWrapperRef}
            >
              <svg
                className="indiaMap"
                viewBox={India.viewBox}
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMidYMid meet"
              >
                {India.locations.map((location) => {
                  const isHighlighted = clickedStates.some((st) => isStateMatching(st, location.name));
                  const isLatestActive = isStateMatching(activeState, location.name);
                  const hasTemples = doesStateHaveTemples(location.name);

                  let stateClass = `indiaMapState ${hasTemples ? "hasTemples" : "noTemples"}`;
                  if (isHighlighted) {
                    stateClass += " selectedState";
                  }
                  if (isLatestActive) {
                    stateClass += " latestActiveState";
                  }

                  return (
                    <path
                      key={location.id}
                      id={location.id}
                      d={location.path}
                      className={stateClass}
                      onMouseEnter={(event) =>
                        handleMouseEnter(event, location)
                      }
                      onMouseLeave={handleMouseLeave}
                      onClick={() =>
                        handleStateClick(location)
                      }
                      role="link"
                      tabIndex={0}
                      aria-label={location.name}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleStateClick(location);
                        }
                      }}
                    />
                  );
                })}
              </svg>

              {/* TOOLTIP */}
              {hoveredState && (() => {
                const isAvailable = doesStateHaveTemples(hoveredState);
                const stateTemples = getStateTemples(hoveredState);
                return (
                  <div
                    className="stateTooltip"
                    style={{
                      left: `${tooltipPosition.left}px`,
                      top: `${tooltipPosition.top}px`,
                    }}
                  >
                    <div className="tooltipTop">
                      <span
                        className={`tooltipDot ${
                          isAvailable ? "activeDot" : "mutedDot"
                        }`}
                      ></span>

                      <div className="tooltipTitleGroup">
                        <span className="tooltipName">{hoveredState}</span>
                        <span
                          className={`tooltipStatusBadge ${
                            isAvailable ? "badgeActive" : "badgeComing"
                          }`}
                        >
                          {isAvailable
                            ? `${stateTemples.length} ${
                                stateTemples.length === 1 ? "Temple" : "Temples"
                              }`
                            : "Coming Soon"}
                        </span>
                      </div>
                    </div>

                    {isAvailable && stateTemples.length > 0 && (
                      <div className="tooltipTemplesList">
                        {stateTemples.slice(0, 3).map((t) => (
                          <div
                            key={t.slug || t.name}
                            className="tooltipTempleRow"
                          >
                            <span className="templeBullet">✦</span>
                            <span className="templeItemName">{t.name}</span>
                          </div>
                        ))}
                        {stateTemples.length > 3 && (
                          <div className="tooltipMoreCount">
                            +{stateTemples.length - 3} more{" "}
                            {stateTemples.length - 3 === 1
                              ? "temple"
                              : "temples"}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="tooltipBottom">
                      <span>
                        {isAvailable
                          ? "Explore Temples in Listing"
                          : "0 temples available"}
                      </span>
                      <span className="tooltipArrowIcon">
                        &rarr;
                      </span>
                    </div>

                    <span className="tooltipArrow"></span>
                  </div>
                );
              })()}
            </div>

            {/* MAP LEGEND */}
            <div className="mapLegend">
              <div className="legendItem">
                <span className="legendDot activeDot"></span>
                <span>Active States with Temples (Click to view listing)</span>
              </div>
              <div className="legendItem">
                <span className="legendDot mutedDot"></span>
                <span>Coming Soon (No temples yet)</span>
              </div>
              {clickedStates.length > 0 && (
                <div className="legendItem selectedLegendItem">
                  <span className="legendDot selectedDot"></span>
                  <span>
                    Highlighted ({clickedStates.length}):{" "}
                    <strong>
                      {clickedStates.slice(-2).join(", ")}
                      {clickedStates.length > 2 ? ` +${clickedStates.length - 2} more` : ""}
                    </strong>
                  </span>
                  <button
                    type="button"
                    className="clearSelectedStateBtn"
                    onClick={handleClearAllClickedStates}
                    title="Clear all state highlights"
                    aria-label="Clear all state highlights"
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>


      {/* ==================================================
          STATS
      ================================================== */}

      <section className="mapStatsSection">

        <div className="container">

          <div className="mapStats">

            <div className="mapStat">

              <strong>
                28
              </strong>

              <span>
                States
              </span>

            </div>


            <div className="statDivider"></div>


            <div className="mapStat">

              <strong>
                8
              </strong>

              <span>
                Union Territories
              </span>

            </div>


            <div className="statDivider"></div>


            <div className="mapStat">

              <strong>
                1000+
              </strong>

              <span>
                Temples
              </span>

            </div>


            <div className="statDivider"></div>


            <div className="mapStat">

              <strong>
                100+
              </strong>

              <span>
                Sacred Cities
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* ==================================================
          REGIONS
      ================================================== */}

      <section className="regionsSection">

        <div className="container">

          <div className="sectionHeading leftHeading">

            <span className="sectionBadge">
              Explore India
            </span>

            <h2>
              Explore by Region
            </h2>

            <p>
              Discover temples and
              spiritual destinations
              region by region.
            </p>

          </div>


          <div className="regionsGrid">

            {regions.map(
              (region) => (

                <div
                  className="regionCard"
                  key={region.title}
                  onClick={() =>
                    navigate(`/listing?region=${encodeURIComponent(region.slug || region.title)}`)
                  }
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/listing?region=${encodeURIComponent(region.slug || region.title)}`);
                    }
                  }}
                >
                  <div className="regionImage">
                    <img
                      src={region.image}
                      alt={region.title}
                      loading="lazy"
                    />
                    <div className="imageOverlay"></div>
                  </div>

                  <div className="regionContent">
                    <h3>{region.title}</h3>
                    <p>{region.description}</p>
                    <span className="regionStates">
                      {region.statesList ? (
                        region.statesList.map((st, i) => (
                          <span
                            key={st}
                            className="regionStateLink"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/listing?search=${encodeURIComponent(st)}`);
                            }}
                            title={`Explore temples in ${st}`}
                          >
                            {st}{i < region.statesList.length - 1 ? " • " : ""}
                          </span>
                        ))
                      ) : (
                        region.states
                      )}
                    </span>

                    <span className="exploreLink">
                      Explore {region.title}
                      <span>→</span>
                    </span>
                  </div>
                </div>

              )
            )}

          </div>

        </div>

      </section>



     

      

      


   

      {/* ==================================================
          FINAL CTA
      ================================================== */}

      <section className="mapFinalCta">

        <div className="ctaGlow"></div>

        <div className="container">

          <div className="ctaContent">

            <span className="ctaIcon">
              ✦
            </span>

            <h2>
              Where Will Your
              <span>
                Journey Begin?
              </span>
            </h2>

            <p>
              Choose a state and discover
              the temples, stories and
              spiritual experiences waiting
              for you.
            </p>

            <button
              type="button"
              onClick={() => {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            >
              Explore India
              <span>
                ↑
              </span>
            </button>

          </div>

        </div>

      </section>

    </main>
  );
};

export default IndiaMap;